import React from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Clock, CreditCard, AlertTriangle, Shield, FileText, Scale, HelpCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { contactInfo } from '../config/contactInfo';

const RefundPolicy: React.FC = () => {
  const sections = [
    {
      icon: Clock,
      title: '1. Cooling-Off Period (CPA Section 44)',
      content: [
        'In accordance with the Consumer Protection Act 68 of 2008 (Section 44), if you subscribed to our Services via an electronic transaction (online), you have the right to cancel your subscription within 5 business days of the date of the transaction',
        'During this cooling-off period, you may cancel for any reason or no reason, without penalty',
        'To exercise this right, notify us in writing (email to ' + contactInfo.email.support + ') within the 5-business-day period',
        'A full refund will be processed within 15 business days of receiving your cancellation notice',
        'Refunds will be made using the same payment method used for the original transaction (via PayFast)'
      ]
    },
    {
      icon: RefreshCw,
      title: '2. Monthly Subscription Cancellation',
      content: [
        'Monthly subscriptions may be cancelled at any time through your account settings or by contacting our support team',
        'Cancellation takes effect at the end of the current billing period; you will retain access to the Services until that date',
        'No refund is provided for the remaining days of the current billing period after cancellation',
        'Once cancelled, your subscription will not automatically renew, and no further charges will be made',
        'You may resubscribe at any time at the then-current pricing'
      ]
    },
    {
      icon: CreditCard,
      title: '3. Annual Subscription Cancellation (CPA Section 14)',
      content: [
        'Annual subscriptions are treated as fixed-term agreements under the Consumer Protection Act (Section 14)',
        'You may cancel an annual subscription at any time by giving 20 business days\' written notice to our support team',
        'Upon early cancellation, you may be subject to a reasonable cancellation penalty as permitted by the CPA, which shall not exceed the lesser of: (a) the subscription fees for the remaining months of the annual term, or (b) an amount reasonably calculated to cover BioAxis\'s direct costs',
        'Where applicable, a pro-rata refund for the unused portion of your annual subscription (less any applicable cancellation penalty) will be processed within 15 business days',
        'BioAxis will provide you with a clear breakdown of any cancellation penalty before processing the cancellation'
      ]
    },
    {
      icon: Shield,
      title: '4. Refund for Service Failures',
      content: [
        'If BioAxis fails to provide the Services as described or if the Services are materially deficient, you may be entitled to a refund under the Consumer Protection Act (Section 54-56)',
        'You must notify BioAxis of any service failure within 30 days of the occurrence',
        'BioAxis will investigate the reported failure and, where confirmed, offer an appropriate remedy which may include: service restoration, credit for the affected period, or a proportional refund',
        'Service interruptions due to scheduled maintenance, force majeure events (including load shedding), or circumstances beyond our reasonable control do not constitute service failures',
        'Our maximum refund liability for service failures shall not exceed the fees paid for the affected period'
      ]
    },
    {
      icon: AlertTriangle,
      title: '5. Non-Refundable Items',
      content: [
        'One-time setup fees or onboarding charges (where applicable) are non-refundable once the setup has been completed',
        'Custom development work or bespoke integrations commissioned by you are non-refundable upon delivery',
        'Fees for additional services or add-ons consumed during the billing period (e.g., data exports, premium support)',
        'Subscriptions terminated by BioAxis due to your breach of the Terms of Service or Acceptable Use Policy',
        'Any discounted or promotional pricing is subject to the specific terms of the promotion'
      ]
    }
  ];

  const faqs = [
    {
      q: 'How long does a refund take to process?',
      a: 'Refunds are processed within 15 business days of approval. The time for the refund to appear in your account depends on your bank or payment provider (typically 3-7 additional business days).'
    },
    {
      q: 'How will I receive my refund?',
      a: 'Refunds are processed via PayFast using the same payment method used for the original transaction. If this is not possible, we will arrange an alternative method (e.g., EFT to your South African bank account).'
    },
    {
      q: 'Can I downgrade instead of cancelling?',
      a: 'Yes. You can downgrade to a lower-tier plan at any time. The change will take effect at the start of your next billing period. Contact support for assistance.'
    },
    {
      q: 'What happens to my data after cancellation?',
      a: 'Your data will be available for export for 30 days after cancellation. After this period, your data will be securely deleted in accordance with our data retention policy and POPIA requirements.'
    },
    {
      q: 'Can I pause my subscription instead of cancelling?',
      a: 'Subscription pausing may be available depending on your plan. Contact our support team to discuss options. Paused subscriptions are not charged during the pause period.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20" style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <RefreshCw className="w-16 h-16 text-primary-400 mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Refund &amp; Cancellation <span className="gradient-text">Policy</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Our refund and cancellation policy is designed to be fair and transparent, in compliance with the Consumer Protection Act 68 of 2008.
            </p>
            <p className="text-sm text-gray-400 mt-4">
              Last updated: February 2026
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Overview */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mb-12"
          >
            <h2 className="text-3xl font-bold mb-6">Overview</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              This Refund &amp; Cancellation Policy ("Policy") governs cancellations, refunds, and related matters for all BioAxis subscription services, including VetAxis, StaffAxis, NoteAxis, and Suite bundles.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              This Policy is governed by South African law, including the Consumer Protection Act 68 of 2008 ("CPA") and the Electronic Communications and Transactions Act 25 of 2002 ("ECT Act"). All prices and refunds are in South African Rand (ZAR).
            </p>
            <div className="bg-primary-900/20 border border-primary-700/30 rounded-xl p-4 mt-6">
              <p className="text-primary-300 text-sm font-medium">
                Your rights under the Consumer Protection Act cannot be limited or excluded by this Policy. Where this Policy conflicts with your CPA rights, the CPA shall prevail.
              </p>
            </div>
          </motion.div>

          {/* Policy Sections */}
          <div className="space-y-8">
            {sections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="glass-effect glass-effect-hover p-8 rounded-2xl"
              >
                <div className="flex items-start gap-4 mb-6">
                  <section.icon className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
                  <h3 className="text-2xl font-bold">{section.title}</h3>
                </div>
                <ul className="space-y-3">
                  {section.content.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-300">
                      <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* How to Cancel */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-12"
          >
            <div className="flex items-start gap-4 mb-6">
              <FileText className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
              <h3 className="text-2xl font-bold">6. How to Cancel</h3>
            </div>
            <div className="text-gray-300 space-y-6">
              <p>You may cancel your subscription using any of the following methods:</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { title: 'Online', desc: 'Log in to your account, navigate to Billing, and select "Cancel Subscription"', icon: '💻' },
                  { title: 'Email', desc: `Send a cancellation request to ${contactInfo.email.support} with your account details`, icon: '📧' },
                  { title: 'Phone', desc: `Call us at ${contactInfo.phone.display} during business hours (${contactInfo.business.hours.weekdays})`, icon: '📞' },
                ].map((method) => (
                  <div key={method.title} className="bg-dark-800/50 border border-dark-700/50 rounded-xl p-4 text-center">
                    <span className="text-2xl">{method.icon}</span>
                    <h4 className="font-semibold text-white mt-2">{method.title}</h4>
                    <p className="text-sm text-gray-400 mt-1">{method.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-400">
                BioAxis will send a written confirmation of your cancellation within 2 business days of receiving your request.
              </p>
            </div>
          </motion.div>

          {/* Disputes */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-8"
          >
            <div className="flex items-start gap-4 mb-6">
              <Scale className="w-8 h-8 text-primary-400 flex-shrink-0 mt-1" />
              <h3 className="text-2xl font-bold">7. Disputes & Consumer Complaints</h3>
            </div>
            <div className="text-gray-300 space-y-4">
              <p>
                If you are dissatisfied with our handling of a cancellation or refund, you may:
              </p>
              <ul className="space-y-3">
                {[
                  'Contact our support team to resolve the matter informally',
                  'Lodge a formal complaint with BioAxis in writing',
                  'Approach the National Consumer Commission (NCC) for assistance',
                  'Refer the matter to the relevant industry Ombud or the Consumer Goods and Services Ombud (CGSO)',
                  'Institute proceedings in any court of competent jurisdiction',
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* FAQs */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-12"
          >
            <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
              <HelpCircle className="w-8 h-8 text-primary-400" />
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="border-b border-dark-700/50 pb-4 last:border-0 last:pb-0">
                  <h4 className="font-semibold text-white mb-2">{faq.q}</h4>
                  <p className="text-gray-400 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-effect p-8 rounded-2xl mt-12 text-center"
          >
            <h3 className="text-2xl font-bold mb-4">Need Help With Billing?</h3>
            <p className="text-gray-300 mb-6">
              Our support team is here to help with any billing, cancellation, or refund enquiries.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href={`mailto:${contactInfo.email.support}`}
                whileHover={{ scale: 1.05 }}
                className="btn-primary"
              >
                Email Support
              </motion.a>
              <Link to="/billing">
                <motion.span
                  whileHover={{ scale: 1.05 }}
                  className="btn-outline inline-block"
                >
                  Manage Billing
                </motion.span>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RefundPolicy;
